import React, { useState } from "react";
import {
  Card,
  Row,
  Col,
  Badge,
  Typography,
  Progress,
  Modal,
  Tooltip,
  Tag,
} from "antd";
import {
  FileAddOutlined,
  UserAddOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined,
  TrophyOutlined,
  ClockCircleOutlined,
  BarChartOutlined,
} from "@ant-design/icons";
import { ResponsiveBar } from "@nivo/bar";
import ReactApexChart from "react-apexcharts";
import { useGetServiceRequestMetricsQuery } from "../../../Redux/service/fsm/FSMDashboard";
import Loader from "../../Helpers/Loader/Loader";
import trans from "../../../utils";
import SectionTitle from "./widgets/SectionTitle";
import NumbersCard from "./widgets/NumbersCard";
import dayjs from "dayjs";
import AlertPopup from "../FMSDashboardPopups/AlertPopup";

const { Title, Text } = Typography;

// Refined color scheme for better contrast and accessibility
const colorScheme = {
  primary: "#0f6936",
  primaryLight: "#16a34a",
  secondary: "#059669",
  warning: "#ea580c",
  info: "#0ea5e9",
  success: "#22c55e",
  neutral: "#64748b",
  light: "#f8fafc",
  dark: "#1e293b",
  surface: "#ffffff",
  border: "#e2e8f0",
};

function formatHour(hour) {
  // Handle both "HH:MM" format and plain hour number
  const hourStr = hour.toString();
  let h;

  if (hourStr.includes(":")) {
    // Extract hour from "HH:MM" format
    h = parseInt(hourStr.split(":")[0]);
  } else {
    h = parseInt(hourStr);
  }

  if (isNaN(h)) return hour;
  const ampm = h >= 12 ? "PM" : "AM";
  const formatted = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${formatted} ${ampm}`;
}

export default function FSMServiceRequestDashboard({ fromDate, toDate }) {
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [expandedChart, setExpandedChart] = useState(null);
  const {
    data: metrics,
    isLoading,
    error,
  } = useGetServiceRequestMetricsQuery({ from: fromDate && fromDate.format("YYYY-MM-DDTHH:mm:ss"), to: toDate && toDate.format("YYYY-MM-DDTHH:mm:ss") },{refetchOnMountOrArgChange: true});

  if (isLoading) return <Loader />;
  if (error)
    return (
      <Card
        style={{
          borderRadius: "12px",
          border: `1px solid ${colorScheme.border}`,
        }}
      >
        <Text type="danger">{error.message}</Text>
      </Card>
    );

  const {
    pipeline,
    top5ServiceTypes,
    requestsByHour,
    avgDurationByServiceType,
  } = metrics || {};
  const {
    new: newReq = 0,
    assigned = 0,
    inProgress = 0,
    completed = 0,
  } = pipeline || {};

  const totalRequests = newReq + assigned + inProgress + completed;
  const completionRate =
    totalRequests > 0 ? Math.round((completed / totalRequests) * 100) : 0;
  const activeRequests = assigned + inProgress;

  const createServiceAlert = (serviceType, count) => ({
  title: serviceType,
  count: count,
  color: colorScheme.primary,
  tooltip: `${trans("View all")} ${serviceType} ${trans("service requests")}`,
  api: "useGetAllRequestsQuery",
  extractData: (response) => response?.data || [],
  frontendFilter: null,
  columns: [
    { title: "Service Type", dataIndex: "serviceType", width: 150 },
    {
      title: "Requester (Customer)",
      dataIndex: "user",
      width: 200,
      render: (value) => value?.name || "- - - - -",
    },
    { title: "Preferred Date", dataIndex: "preferredTime", width: 150, render: (value) => value ? dayjs(value).format("DD/MM/YYYY") : "- - - - -" },
    { title: "Created at", dataIndex: "createdAt", width: 150, render: (value) => value ? dayjs(value).format("DD/MM/YYYY") : "- - - - -" },
  ],
  query: {
    page: 1,
    limit: 10,
    queryString: `&serviceType=${serviceType}`,
    fromDate: fromDate ? fromDate.format("YYYY-MM-DDTHH:mm:ss") : null,
    toDate: toDate ? toDate.format("YYYY-MM-DDTHH:mm:ss") : null,
  },
  extractTotal: (response) => response?.total || 0,
});

  const pipelineSteps = [
    {
      title: trans("New"),
      icon: <FileAddOutlined />,
      count: newReq,
      color: colorScheme.neutral,
      tooltip: trans("Recently created service requests awaiting assignment"),
      api: "useGetAllRequestsQuery",
      extractData: (response) => response?.data || [],
      frontendFilter: null,
      columns: [
        { title: trans("Service Type"), dataIndex: "serviceType", width: 150 },
        {
          title: trans("Requester (Customer)"),
          dataIndex: "user",
          width: 200,
          render: (value) => value?.name || "- - - - -",
        },
        { title: trans("Preferred Date"), dataIndex: "preferredTime", width: 150, render: (value) => value ? dayjs(value).format("DD/MM/YYYY") : "- - - - -" },
        { title: trans("Created At"), dataIndex: "createdAt", width: 150, render: (value) => value ? dayjs(value).format("DD/MM/YYYY") : "- - - - -" },
      ],
      query: {
        page: 1,
        limit: 10,
        queryString: "&status=pending",
        fromDate: fromDate ? fromDate.format("YYYY-MM-DDTHH:mm:ss") : null,
        toDate: toDate ? toDate.format("YYYY-MM-DDTHH:mm:ss") : null,
      },
      extractTotal: (response) => response?.total || 0,
    },
    {
      title: trans("Assigned"),
      icon: <UserAddOutlined />,
      count: assigned,
      color: colorScheme.info,
      tooltip: trans("Requests assigned to team members for action"),
      api: "useGetAllRequestsQuery",
      extractData: (response) => response?.data || [],
      frontendFilter: null,
      columns: [
        { title: trans("Service Type"), dataIndex: "serviceType", width: 150 },
        {
          title: trans("Requester (Customer)"),
          dataIndex: "user",
          width: 200,
          render: (value) => value?.name || "- - - - -",
        },
        { title: trans("Preferred Date"), dataIndex: "preferredTime", width: 150, render: (value) => value ? dayjs(value).format("DD/MM/YYYY") : "- - - - -" },
        { title: trans("Created At"), dataIndex: "createdAt", width: 150, render: (value) => value ? dayjs(value).format("DD/MM/YYYY") : "- - - - -" },
      ],
      query: {
        page: 1,
        limit: 10,
        queryString: "&status=assigned",
        fromDate: fromDate ? fromDate.format("YYYY-MM-DDTHH:mm:ss") : null,
        toDate: toDate ? toDate.format("YYYY-MM-DDTHH:mm:ss") : null,
      },
      extractTotal: (response) => response?.total || 0,
    },
    {
      title: trans("In Progress"),
      icon: <PlayCircleOutlined />,
      count: inProgress,
      color: colorScheme.warning,
      tooltip: trans("Requests currently being handled by the team"),
      api: "useGetAllRequestsQuery",
      extractData: (response) => response?.data || [],
      frontendFilter: null,
      columns: [
        { title: trans("Service Type"), dataIndex: "serviceType", width: 150 },
        {
          title: trans("Requester (Customer)"),
          dataIndex: "user",
          width: 200,
          render: (value) => value?.name || "- - - - -",
        },
        { title: trans("Preferred Date"), dataIndex: "preferredTime", width: 150, render: (value) => value ? dayjs(value).format("DD/MM/YYYY") : "- - - - -" },
        { title: trans("Created At"), dataIndex: "createdAt", width: 150, render: (value) => value ? dayjs(value).format("DD/MM/YYYY") : "- - - - -" },
      ],
      query: {
        page: 1,
        limit: 10,
        queryString: "&status=inProgress",
        fromDate: fromDate ? fromDate.format("YYYY-MM-DDTHH:mm:ss") : null,
        toDate: toDate ? toDate.format("YYYY-MM-DDTHH:mm:ss") : null,
      },
      extractTotal: (response) => response?.total || 0,
    },
    {
      title: trans("Completed"),
      icon: <CheckCircleOutlined />,
      count: completed,
      color: colorScheme.success,
      tooltip: trans("Requests successfully completed and closed"),
      api: "useGetAllRequestsQuery",
      extractData: (response) => response?.data || [],
      frontendFilter: null,
      columns: [
        { title: trans("Service Type"), dataIndex: "serviceType", width: 150 },
        {
          title: trans("Requester (Customer)"),
          dataIndex: "user",
          width: 200,
          render: (value) => value?.name || "- - - - -",
        },
        { title: trans("Preferred Date"), dataIndex: "preferredTime", width: 150, render: (value) => value ? dayjs(value).format("DD/MM/YYYY") : "- - - - -" },
        { title: trans("Created At"), dataIndex: "createdAt", width: 150, render: (value) => value ? dayjs(value).format("DD/MM/YYYY") : "- - - - -" },
      ],
      query: {
        page: 1,
        limit: 10,
        queryString: "&status=completed",
        fromDate: fromDate ? fromDate.format("YYYY-MM-DDTHH:mm:ss") : null,
        toDate: toDate ? toDate.format("YYYY-MM-DDTHH:mm:ss") : null,
      },
      extractTotal: (response) => response?.total || 0,
    },
  ];

  const pieData = pipelineSteps
    .filter((s) => s.count > 0)
    .map((s) => ({
      id: s.title,
      label: s.title,
      value: s.count,
      color: s.color,
    }));

  const barData = Object.entries(requestsByHour || {})
    .filter(([hour, count]) => count > 0) // Only show hours with data
    .map(([hour, count]) => ({
      hour: formatHour(hour),
      requests: count,
    }));

  const topServices = (top5ServiceTypes || []).slice(0, 4); // Show top 4 for compact view

  // ApexCharts configuration for Hourly Distribution
  const hourlyDistributionOptions = {
    chart: {
      type: "bar",
      fontFamily: "'Cairo', sans-serif !important",
      toolbar: { show: false },
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 800,
      },
    },
    colors: ["#096DD9"],
    plotOptions: {
      bar: {
        borderRadius: 4,
        borderRadiusApplication: "end",
        columnWidth: "60%",
        dataLabels: {
          position: "top",
        },
      },
    },
    dataLabels: {
      enabled: true,
      offsetY: -20,
      style: {
        fontSize: "10px",
        colors: [colorScheme.dark],
      },
    },
    xaxis: {
      categories: barData.map((item) => item.hour),
      labels: {
        rotate: -45,
        style: {
          colors: colorScheme.neutral,
          fontSize: "10px",
        },
      },
    },
    yaxis: {
      title: {
        text: trans("Requests"),
        style: {
          color: colorScheme.dark,
          fontSize: "12px",
          fontWeight: "600",
        },
      },
      labels: {
        style: {
          colors: colorScheme.neutral,
          fontSize: "10px",
        },
      },
    },
    grid: {
      borderColor: colorScheme.border,
      strokeDashArray: 3,
    },
    tooltip: {
      theme: "light",
      y: {
        formatter: function (val) {
          return val + " " + trans("requests");
        },
      },
    },
  };

  // ApexCharts configuration for Duration by Service Type
  const durationBarOptions = {
    chart: {
      type: "bar",
      fontFamily: "'Cairo', sans-serif !important",
      toolbar: { show: false },
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 800,
      },
    },
    colors: [colorScheme.info],
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 4,
        dataLabels: {
          position: "top",
        },
      },
    },
    dataLabels: {
      enabled: true,
      offsetX: -6,
      style: {
        fontSize: "10px",
        colors: ["#fff"],
      },
    },
    xaxis: {
      categories: Object.keys(avgDurationByServiceType || {}),
      labels: {
        style: {
          colors: colorScheme.neutral,
          fontSize: "10px",
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: colorScheme.neutral,
          fontSize: "10px",
        },
      },
    },
    grid: {
      borderColor: colorScheme.border,
      strokeDashArray: 3,
    },
    tooltip: {
      theme: "light",
      y: {
        formatter: function (val) {
          return val;
        },
      },
    },
  };

  // Expanded Chart Modal Component
  const ExpandedChartModal = () => {
    return (
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            {expandedChart === "hourly" && (
              <>
                <ClockCircleOutlined
                  style={{
                    fontSize: "16px",
                    color: colorScheme.primary,
                    marginInlineEnd: "8px",
                  }}
                />
                {trans("Hourly Request Distribution - Expanded View")}
              </>
            )}
            {expandedChart === "duration" && (
              <>
                <BarChartOutlined
                  style={{
                    fontSize: "16px",
                    color: colorScheme.primary,
                    marginInlineEnd: "8px",
                  }}
                />
                {trans("Average Completion Duration - Expanded View")}
              </>
            )}
          </div>
        }
        open={expandedChart !== null}
        onCancel={() => setExpandedChart(null)}
        width="90%"
        style={{ top: 20 }}
        footer={null}
        bodyStyle={{ padding: "20px" }}
      >
        {expandedChart === "hourly" && barData.length > 0 && (
          <div style={{ height: "500px" }}>
            <ReactApexChart
              options={{
                ...hourlyDistributionOptions,
                chart: {
                  ...hourlyDistributionOptions.chart,
                  height: 500,
                },
              }}
              series={[
                {
                  name: trans("Requests"),
                  data: barData.map((item) => item.requests),
                },
              ]}
              type="bar"
              height={500}
            />
          </div>
        )}

        {expandedChart === "duration" &&
          avgDurationByServiceType &&
          Object.keys(avgDurationByServiceType).length > 0 && (
            <div style={{ height: "500px" }}>
              <ReactApexChart
                options={{
                  ...durationBarOptions,
                  chart: {
                    ...durationBarOptions.chart,
                    height: 500,
                  },
                }}
                series={[
                  {
                    name: trans("Duration"),
                    data: Object.values(avgDurationByServiceType),
                  },
                ]}
                type="bar"
                height={500}
              />
            </div>
          )}
      </Modal>
    );
  };

  const durationData = Object.entries(avgDurationByServiceType || {}).map(
    ([service, duration]) => ({
      service,
      duration: parseFloat(duration), // extract the number
    })
  );

  return (
    <div
      style={{
        minHeight: "100%",
        padding: "16px",
        borderRadius: "12px",
        border: `1px solid ${colorScheme.border}`,

        display: "flex", // 👈 enable flex
        flexDirection: "column", // 👈 stack children vertically
      }}
    >
      <SectionTitle
        title={"Service Requests"}
        subtitle={"Pipeline Overview"}
        imgSrc={`${process.env.PUBLIC_URL}/assets/folder.svg`}
        imgBgColor="#F9F0FF"
        filterApplied={fromDate && toDate ? true : false}
        filterText={`From ${dayjs(fromDate).format("DD/MM/YYYY")} - ${dayjs(toDate).format("DD/MM/YYYY")}`}
      />

      {/* Pipeline Cards - Matching TaskDistribution style */}
      <Row gutter={[12, 12]} style={{ marginBottom: "20px" }}>
        {pipelineSteps.map((step, i) => (
          <Col span={6} key={i}>
            <NumbersCard
              title={step.title}
              value={step.count}
              suffix={step.suffix}
              color={step.color}
              tooltip={step.tooltip}
              onClick={() => step.api && setSelectedAlert(step)}
              popup={step.api ? true : false}
            />
          </Col>
        ))}
      </Row>

      {/* Charts Section */}
      <Row gutter={[16, 16]} style={{ marginBottom: "20px" }}>
        {/* Pipeline Distribution */}
        <Col span={12}>
          <Tooltip
            title={trans(
              "Shows the progress of service requests through different workflow stages"
            )}
          >
            <Card
              style={{
                borderRadius: "12px",
                border: "none",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                height: "100%",
              }}
              bodyStyle={{ padding: "16px" }}
              title={
                <Text style={{ fontSize: "14px", fontWeight: "600" }}>
                  {trans("Pipeline Status")}
                </Text>
              }
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                {pipelineSteps.map((step, index) => {
                  const percentage =
                    totalRequests > 0
                      ? Math.round((step.count / totalRequests) * 100)
                      : 0;

                  return (
                    <div key={index} style={{ width: "100%", cursor: step.api ? "pointer" : "default" }} onClick={() => step.api && setSelectedAlert(step)}>
                      {/* Title & Numbers */}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "4px",
                        }}
                      >
                        <Text style={{ fontSize: "13px", fontWeight: "500" }}>
                          {step.title}
                        </Text>
                        <Text style={{ fontSize: "13px", fontWeight: "500" }}>
                          {step.count} / {percentage}%
                        </Text>
                      </div>

                      {/* AntD Progress Bar */}
                      <Progress
                        percent={percentage}
                        strokeColor={step.color}
                        showInfo={false}
                        trailColor="#f0f0f0"
                        size={["100%", 20]}
                      />
                    </div>
                  );
                })}
              </div>
            </Card>
          </Tooltip>
        </Col>

        {/* Top Service Types */}
        <Col span={isExpanded ? 12 : 24}>
          <Tooltip
            title={trans(
              "Highlights the most frequently requested service categories"
            )}
          >
            <Card
              style={{
                borderRadius: "12px",
                border: "none",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              }}
              bodyStyle={{ padding: "16px" }}
              title={
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ fontSize: "14px", fontWeight: "600" }}>
                    {trans("Top Service Types")}
                  </span>
                </div>
              }
            >
              <div style={{ height: "250px", overflowY: "auto" }}>
                {topServices.length > 0 ? (
                  topServices.map((type, i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "12px 16px",
                        marginBottom: "8px",
                        background: i === 0 ? "#EAF4EC" : colorScheme.surface,
                        borderRadius: "8px",
                        border:
                          i === 0 ? `` : `1px solid ${colorScheme.border}`,
                        transition: "all 0.2s ease",
                        cursor: "pointer",
                      }}
                      onClick={() => setSelectedAlert(createServiceAlert(type.type, type.count))} // Add onClick handler
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          gap: 8,
                        }}
                      >
                        <Badge
                          count={i + 1}
                          style={{
                            backgroundColor: i === 0 ? "#ffffff" : "#FAFAFA",
                            fontSize: "11px",
                            fontWeight: "500",
                            color: i === 0 ? "#2E9245" : "#195026",
                          }}
                        />
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <Text
                            style={{
                              fontWeight: i === 0 ? "600" : "500",
                              fontSize: "13px",
                              color: colorScheme.dark,
                              textTransform: "capitalize",
                            }}
                          >
                            {type.type}
                          </Text>
                        </div>
                      </div>
                      <Badge
                        count={type.count}
                        style={{
                          backgroundColor:
                            i === 0 ? colorScheme.primary : colorScheme.neutral,
                          fontSize: "11px",
                          fontWeight: "500",
                        }}
                      />
                    </div>
                  ))
                ) : (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      flexDirection: "column",
                      color: colorScheme.neutral,
                    }}
                  >
                    <TrophyOutlined
                      style={{
                        fontSize: "32px",
                        marginBottom: "12px",
                        opacity: 0.3,
                      }}
                    />
                    <Text style={{ color: colorScheme.neutral }}>
                      {trans("No service type data available")}
                    </Text>
                  </div>
                )}
              </div>
            </Card>
          </Tooltip>
        </Col>
      </Row>

      {/* Enhanced Expanded View - Side by Side Charts */}
      {isExpanded && (
        <Row
          gutter={[16, 16]}
          style={{
            flexGrow: 1, // 👈 makes it take remaining space
            display: "flex", // ensure content fills vertically
            flexDirection: "row",
            height: "100%",
          }}
        >
          {/* Hourly Distribution */}
          {barData.length > 0 && (
            <Col span={12}>
              <Tooltip
                title={trans(
                  "Shows how service requests are distributed throughout the day"
                )}
              >
                <Card
                  style={{
                    borderRadius: "12px",
                    border: "none",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    height: "100%",
                  }}
                  bodyStyle={{ padding: "16px" }}
                  title={
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <span style={{ fontSize: "14px", fontWeight: "600" }}>
                          {trans("Hourly Request Distribution")}
                        </span>
                      </div>
                    </div>
                  }
                >
                  <div style={{ height: 350 }}>
                    <ResponsiveBar
                      data={barData}
                      keys={["requests"]}
                      indexBy="hour"
                      margin={{ top: 20, right: 30, bottom: 60, left: 50 }}
                      padding={0.3}
                      colors={["#096DD9"]}
                      borderRadius={4}
                      borderRadiusApplication={"end"}
                      axisBottom={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: -45,
                        legend: trans("Time of Day"),
                        legendPosition: "middle",
                        legendOffset: 50,
                      }}
                      axisLeft={{
                        tickSize: 5,
                        tickPadding: 5,
                        legend: trans("Requests"),
                        legendPosition: "middle",
                        legendOffset: -40,
                      }}
                      tooltip={({ indexValue, value }) => (
                        <div
                          style={{
                            background: "white",
                            padding: "8px 12px",
                            border: "1px solid #e2e8f0",
                            borderRadius: "6px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                            fontSize: "13px",
                          }}
                        >
                          <div style={{ fontWeight: "600" }}>{indexValue}</div>
                          <div style={{ color: colorScheme.neutral }}>
                            {value} {trans("requests")}
                          </div>
                        </div>
                      )}
                      animate={true}
                      motionStiffness={120}
                      motionDamping={20}
                      labelTextColor="#ffffff"
                    />
                  </div>
                </Card>
              </Tooltip>
            </Col>
          )}

          {/* Average Duration */}
          {avgDurationByServiceType &&
            Object.keys(avgDurationByServiceType).length > 0 && (
              <Col span={12}>
                <Tooltip
                  title={trans(
                    "Displays the average time taken to complete each service type"
                  )}
                >
                  <Card
                    style={{
                      borderRadius: "12px",
                      border: "none",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                      height: "100%",
                    }}
                    bodyStyle={{ padding: "16px", height: "100%" }}
                    title={
                      <Text style={{ fontSize: "14px", fontWeight: "600" }}>
                        {trans("Average Completion Duration")}
                      </Text>
                    }
                  >
                    <div style={{ height: 350 }}>
                      <ResponsiveBar
                        data={durationData}
                        keys={["duration"]}
                        indexBy="service"
                        margin={{ top: 20, right: 30, bottom: 60, left: 50 }}
                        padding={0.3}
                        colors={[colorScheme.info]}
                        borderRadius={4}
                        borderRadiusApplication={"end"}
                        axisBottom={{
                          tickSize: 5,
                          tickPadding: 5,
                          tickRotation: -45,
                          legend: trans("Service Type"),
                          legendPosition: "middle",
                          legendOffset: 50,
                        }}
                        axisLeft={{
                          tickSize: 5,
                          tickPadding: 5,
                          legend: trans("Avg Duration (hrs)"),
                          legendPosition: "middle",
                          legendOffset: -40,
                        }}
                        tooltip={({ indexValue, value }) => (
                          <div
                            style={{
                              background: "white",
                              padding: "8px 12px",
                              border: "1px solid #e2e8f0",
                              borderRadius: "6px",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                              fontSize: "13px",
                            }}
                          >
                            <div style={{ fontWeight: "600" }}>
                              {indexValue}
                            </div>
                            <div style={{ color: colorScheme.neutral }}>
                              {value} {trans("requests")}
                            </div>
                          </div>
                        )}
                        animate={true}
                        motionStiffness={120}
                        motionDamping={20}
                        labelTextColor="#ffffff"
                      />
                    </div>
                  </Card>
                </Tooltip>
              </Col>
            )}
        </Row>
      )}

      {/* Expanded Chart Modal */}
      <ExpandedChartModal />
      {selectedAlert && (
        <AlertPopup
          alert={selectedAlert}
          onClose={() => setSelectedAlert(null)}
          fromDate={fromDate}
          toDate={toDate}
        />
      )}
    </div>
  );
}
